import { toStoredPhoto } from "../services/checklistPhotoService";

// Bridges the UI checklist/entry shape (used across the maintenance-task
// builder and the notification fill-out screens) to the backend's wire DTOs.
//
// The two directions are NOT symmetric:
// - Read model (GET responses): name/comment/item title/description/
//   answertitle/phototitle are per-language dicts, e.g. { de: "...", en: "..." }.
// - Write DTO (create/update payloads): the same fields are flat strings plus
//   a single top-level `lang`, matching how the rest of this app already
//   sends localized content.

// Photos on answers (report.items[].photos, uploaded under the in-progress
// report's nodeid — see ChecklistsTab). With this off, the builder shows the
// "photos required" controls disabled and the fill-out view hides the upload
// and never requires photos.
export const CHECKLIST_ATTACHMENTS_ENABLED = true;
// (Reference photos per entry — `checklist_item_photos`, see
// services/checklistPhotoService.js — are separate from this and always on.)

export const ANSWER_TYPE_TO_WIRE = { checkbox: 0, number: 1, text: 2 };
export const ANSWER_TYPE_FROM_WIRE = { 0: "checkbox", 1: "number", 2: "text" };

// GET /checklists?maintenancetasknodeid=... / ?legalentitynodeid=&template=true
// are documented as returning a flat Checklist[], but the current backend
// implementation (ChecklistService.List()) returns the raw GraphQL-proxy
// envelope unshaped: { result: [{ data: [{ <rootKey>: { checklist: [...] } }] }] }.
// This extracts the array from either shape, so the FE keeps working if/when
// that gets fixed to match the documented contract.
export const extractChecklistListFromResponse = (payload, rootKey) => {
  if (Array.isArray(payload)) return payload;
  const root = payload?.result?.[0]?.data?.[0]?.[rootKey];
  if (Array.isArray(root?.checklist)) return root.checklist;
  // GET /checklists?maintenancetasknodeid= returns the embedded-graph form,
  // haschecklist: [{ checklist: {...} }].
  if (Array.isArray(root?.haschecklist)) {
    return root.haschecklist.map((entry) => entry?.checklist || entry).filter(Boolean);
  }
  return [];
};

// GET /checklists?legalentitynodeid=&template=true&lang= has a DIFFERENT raw
// envelope shape than the maintenancetask-scoped list above: KH returns
// legalentity.haschecklisttemplate: [{ checklist: {...} }] — the same
// hasX: [{x: {...}}] embedded-graph convention used for a maintenancetask's
// own haschecklist/haschecklistreport, not a flat checklist[] sitting
// directly on the root key. Flattens to a plain Checklist[].
export const extractChecklistTemplatesFromResponse = payload => {
  if (Array.isArray(payload)) return payload;
  const wrapped = payload?.result?.[0]?.data?.[0]?.legalentity?.haschecklisttemplate;
  return Array.isArray(wrapped)
    ? wrapped.map(entry => entry?.checklist || entry).filter(Boolean)
    : [];
};

// GET /checklists/:nodeid/reports (ChecklistService.ListReports()) has the
// same raw-envelope problem, one level deeper: a relation traversal's result
// comes back keyed by the RELATION IDENTIFIER (haschecklistreport), each
// element wrapping its data under the alias (checklistreport) — see
// ChecklistService.GetOne()'s comment on this. Flattens to a plain
// ChecklistReport[] (each with nodeid/items), tolerating an already-flat
// array too.
export const extractChecklistReportsFromResponse = payload => {
  if (Array.isArray(payload)) return payload;
  const wrapped = payload?.result?.[0]?.data?.[0]?.checklist?.haschecklistreport;
  return Array.isArray(wrapped)
    ? wrapped.map(entry => entry?.checklistreport || entry).filter(Boolean)
    : [];
};

let localKeySeed = 0;

// Client-only identity for React `key=`/list bookkeeping. Never sent to the
// server — the real identity is `nodeid` (checklist) / `id` (item), assigned
// by the backend once a checklist has actually been persisted.
export const createLocalItemKey = () =>
  `checklist-local-${Date.now()}-${(localKeySeed++).toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const unwrapLocalizedText = (value, lang) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value[lang] === "string") return value[lang];
    const firstString = Object.values(value).find(v => typeof v === "string");
    return firstString || "";
  }
  return "";
};

const normalizeEntryFromServer = (item = {}, lang) => ({
  id: item.id,
  _isNew: false,
  title: unwrapLocalizedText(item.title, lang),
  description: unwrapLocalizedText(item.description, lang),
  answerType: ANSWER_TYPE_FROM_WIRE[item.answertype] || "checkbox",
  answerTitle: unwrapLocalizedText(item.answertitle, lang),
  uploadTitle: unwrapLocalizedText(item.phototitle, lang) || "Foto(s) hochladen",
  uploadRequired: Boolean(item.photouploadrequired),
  responseRequired: Boolean(item.answerrequired),
  historyVisible: Boolean(item.showpastanswers),
  pastanswer: item.pastanswer || null,
  // Reference photos of the entry (`checklist_item_photos`, each read with a
  // signed, expiring `url`). Left undefined when the read didn't include the
  // field, so a save never sends [] for photos it simply didn't see.
  referencePhotos: Array.isArray(item.checklist_item_photos)
    ? item.checklist_item_photos
    : undefined,
  // Whether any report has ever recorded a real (isAnswered: true) answer
  // for this item. Only computed by normalizeChecklistFromEmbedded (which has
  // report data); defaults false for the flat dedicated-endpoint shape used
  // by the builder. (The "Vergangene Antworten" button follows
  // historyVisible, the author's setting, not this.)
  hasAnswerHistory: Boolean(item.hasAnswerHistory)
});

export const normalizeChecklistFromServer = (checklist = {}, lang) => ({
  nodeid: checklist.nodeid,
  _localKey: createLocalItemKey(),
  title: unwrapLocalizedText(checklist.name, lang),
  comment: unwrapLocalizedText(checklist.comment, lang),
  isTemplate: Boolean(checklist.hasTemplate),
  legalentitynodeid: checklist.legalentitynodeid,
  isCompleted: false,
  entries: Array.isArray(checklist.items)
    ? checklist.items.map(item => normalizeEntryFromServer(item, lang))
    : []
});

export const normalizeChecklistsFromServer = (list, lang) =>
  Array.isArray(list)
    ? list.map(checklist => normalizeChecklistFromServer(checklist, lang))
    : [];

// A `maintenancetask` object (wherever one appears — a notification's
// `hasmaintenancetask[0].maintenancetask`, a machine's, etc.) carries its
// checklists inline as `haschecklist: [{ checklist: {...} }]`, following the
// same `hasX: [{x: {...}}]` graph convention as `hasworkflow`/`hasattachment`
// elsewhere in this app. Each embedded checklist also carries its full report
// history inline via `haschecklistreport: [{ checklistreport: {items,...} }]`
// — so past answers come from there, not a `pastanswer` field per item (that
// field is specific to the dedicated `GET /checklists/:nodeid` endpoint).
// Unlike the dedicated-endpoint read model, `name`/`comment` here are plain
// strings, not per-language dicts — `unwrapLocalizedText` handles both.
const mergeReportsIntoPastanswers = (haschecklistreport, answersSince, notificationNodeId) => {
  const pastanswers = {};

  (haschecklistreport || []).forEach(reportWrapper => {
    const report = reportWrapper?.checklistreport || reportWrapper;

    (report?.items || []).forEach(item => {
      // Only this occurrence's answers when a start is given (see
      // normalizeChecklistFromEmbedded's `answersSince`).
      if (
        answersSince &&
        !(item.isAnswered && answerBelongsToOccurrence(report, item, answersSince, notificationNodeId))
      ) return;
      const existing = pastanswers[item.id];
      if (!existing || (item.timestamp || 0) >= (existing.timestamp || 0)) {
        pastanswers[item.id] = item;
      }
    });
  });

  return pastanswers;
};

// A checklist is "completed" when at least one of its reports has a valid
// answer for every REQUIRED item — matching the backend's own hard-validation
// rules (SaveAnswer/CreateReport), not merely "a report record exists" (that
// happens on the very first partial "Antwort speichern" click). Trusts the
// report item's own `isAnswered` flag rather than checking value presence: a
// checkbox's `checkboxvalue: false` is a real, valid value for "never
// actually answered" as much as for "explicitly left unchecked", and only
// the backend's own flag tells the two apart.
const itemHasValidAnswer = (item, answer) => Boolean(answer?.isAnswered);

const isReportComplete = (items, report) => {
  const answersById = new Map((report?.items || []).map(item => [item.id, item]));
  return (items || []).every(item => {
    if (!item.answerrequired) return true;
    return itemHasValidAnswer(item, answersById.get(item.id));
  });
};

// `answersSince` (ms): the start of the occurrence being worked on — the
// Meldung's `created` on the fill-out view. A checklist's reports hang on the
// checklist, not on a Meldung, so a recurring task's next occurrence would
// otherwise show (and prefill) last time's answers as already done. With it,
// `pastanswer` and `isCompleted` cover only answers saved since then
// (Mathias, 30.10.2026: no prefill, the count starts at 0). Without it (the
// builder, the protocol pages), every report counts as before.
// `notificationNodeId`: the Meldung being worked on; reports linked to it count
// whatever their time (see answerBelongsToOccurrence).
export const normalizeChecklistFromEmbedded = (hasChecklistEntry, lang, answersSince, notificationNodeId) => {
  const checklist = hasChecklistEntry?.checklist || hasChecklistEntry || {};
  const pastanswers = mergeReportsIntoPastanswers(
    checklist.haschecklistreport,
    answersSince,
    notificationNodeId
  );
  const reports = (checklist.haschecklistreport || []).map(
    wrapper => wrapper?.checklistreport || wrapper
  );

  const answeredItemIds = new Set();
  reports.forEach(report => {
    (report?.items || []).forEach(item => {
      if (item.isAnswered === true) answeredItemIds.add(item.id);
    });
  });

  return {
    nodeid: checklist.nodeid,
    _localKey: createLocalItemKey(),
    title: unwrapLocalizedText(checklist.name, lang),
    comment: unwrapLocalizedText(checklist.comment, lang),
    isTemplate: Boolean(checklist.hasTemplate),
    legalentitynodeid: checklist.legalentitynodeid,
    isCompleted: answersSince
      ? Object.keys(pastanswers).length > 0 &&
        (checklist.items || []).every(
          item => !item.answerrequired || itemHasValidAnswer(item, pastanswers[item.id])
        )
      : reports.some(report => isReportComplete(checklist.items || [], report)),
    answersSince: answersSince || null,
    notificationnodeid: notificationNodeId || null,
    entries: Array.isArray(checklist.items)
      ? checklist.items.map(item =>
          normalizeEntryFromServer(
            {
              ...item,
              pastanswer: answersSince
                ? pastanswers[item.id] || null
                : pastanswers[item.id] || item.pastanswer,
              hasAnswerHistory: answeredItemIds.has(item.id)
            },
            lang
          )
        )
      : []
  };
};

// The start of a Meldung's occurrence in ms, for `answersSince`: its
// `created` (set when the backend creates the Meldung), or undefined.
export const occurrenceStart = notification => {
  const created = notification?.notification?.created ?? notification?.created;
  const ms = typeof created === "number" ? created : Date.parse(created);
  return Number.isFinite(ms) && ms > 0 ? ms : undefined;
};

// Whether an answer belongs to the Meldung being worked on. A report linked to
// a Meldung (backend, Rashid 02.11.2026) counts for that Meldung only. An
// unlinked one (saved before the link existed, or by an older app without
// `notificationnodeid`) still counts by its time, from `answersSince` on.
export const answerBelongsToOccurrence = (report, item, answersSince, notificationNodeId) => {
  const linkedTo = report?.notificationnodeid;
  if (linkedTo && notificationNodeId) return linkedTo === notificationNodeId;
  return !answersSince || (item?.timestamp || 0) >= answersSince;
};

export const normalizeChecklistsFromMaintenanceTask = (maintenanceTask, lang, answersSince, notificationNodeId) =>
  Array.isArray(maintenanceTask?.haschecklist)
    ? maintenanceTask.haschecklist.map(entry =>
        normalizeChecklistFromEmbedded(entry, lang, answersSince, notificationNodeId)
      )
    : [];

const toWireEntry = (entry, lang) => {
  const wireEntry = {
    // ChecklistItemDto.lang is required PER ITEM on the backend (not just
    // once at the checklist level) — omitting it makes the server build the
    // localized dict as this.localized(undefined, value), producing a
    // broken { "undefined": "..." } key instead of { "de": "..." }.
    lang,
    title: (entry.title || "").trim(),
    description: (entry.description || "").trim(),
    answertype: ANSWER_TYPE_TO_WIRE[entry.answerType] ?? ANSWER_TYPE_TO_WIRE.checkbox,
    answertitle: (entry.answerTitle || "").trim(),
    phototitle: (entry.uploadTitle || "").trim(),
    photouploadrequired: Boolean(entry.uploadRequired),
    answerrequired: Boolean(entry.responseRequired),
    showpastanswers: Boolean(entry.historyVisible)
  };

  // Sent on every save path: PUT /maintenancetasks recreates the checklists
  // and would drop photos an item doesn't carry. Only the stored fields go;
  // the read `url` and local previews stay here.
  if (Array.isArray(entry.referencePhotos)) {
    wireEntry.checklist_item_photos = entry.referencePhotos.map(toStoredPhoto);
  }

  // Omit `id` for items created client-side and never persisted, so the
  // backend server-assigns one. Keep it for anything we already know.
  // (entry.id itself is always populated — real once persisted, a local
  // placeholder before that — `_isNew` is the sole real/local gate.)
  if (!entry._isNew) {
    wireEntry.id = entry.id;
  }

  return wireEntry;
};

// `legalentitynodeid` is the caller's current legal entity (same value the
// maintenancetask payload itself carries), not checklist.legalentitynodeid —
// that field is only ever populated on an already-saved template checklist,
// so every other checklist (the common case) would otherwise send none at all.
export const toWireChecklist = (checklist, lang, legalentitynodeid) => {
  const wireChecklist = {
    lang,
    name: (checklist.title || "").trim(),
    legalentitynodeid,
    items: Array.isArray(checklist.entries)
      ? checklist.entries.map(entry => toWireEntry(entry, lang))
      : []
  };

  if (checklist.comment) {
    wireChecklist.comment = checklist.comment.trim();
  }

  // Informational only per the backend contract (every update recreates the
  // checklist regardless) — included for continuity/audit, not as an edit signal.
  if (checklist.nodeid) {
    wireChecklist.nodeid = checklist.nodeid;
  }

  if (checklist.isTemplate) {
    wireChecklist.hasTemplate = true;
  }

  return wireChecklist;
};

// For PUT /checklists/:nodeid (standalone, in-place edit — the nodeid goes in
// the URL, and UpdateChecklistDto has no hasTemplate/legalentitynodeid).
export const toWireChecklistUpdate = (checklist, lang) => {
  const wireChecklist = {
    lang,
    name: (checklist.title || "").trim(),
    items: Array.isArray(checklist.entries)
      ? checklist.entries.map(entry => toWireEntry(entry, lang))
      : []
  };

  if (checklist.comment) {
    wireChecklist.comment = checklist.comment.trim();
  }

  return wireChecklist;
};

export const createLocalChecklistItem = () => ({
  id: createLocalItemKey(),
  _isNew: true,
  title: "",
  description: "",
  answerType: "checkbox",
  answerTitle: "",
  uploadTitle: "Foto(s) hochladen",
  // Off by default: the backend refuses an answer without photos as soon as
  // this is set, even for an optional item — the author opts in.
  uploadRequired: false,
  responseRequired: true,
  historyVisible: false,
  pastanswer: null,
  referencePhotos: []
});

export const createLocalChecklist = () => ({
  nodeid: undefined,
  _localKey: createLocalItemKey(),
  title: "",
  comment: "",
  isTemplate: false,
  legalentitynodeid: undefined,
  entries: []
});

// "Using" a template: copy its content into a brand-new local checklist,
// dropping every item id so the backend generates fresh ones on save.
export const fromTemplateToNewChecklist = template => ({
  ...createLocalChecklist(),
  title: template?.title || "",
  comment: template?.comment || "",
  entries: Array.isArray(template?.entries)
    ? template.entries.map(entry => ({
        ...entry,
        id: createLocalItemKey(),
        _isNew: true,
        pastanswer: null
      }))
    : []
});
